import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Eye, MousePointerClick, Clock } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function absShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function Row({ label, latest, testidPrefix }) {
  if (!latest) {
    return (
      <div className="flex items-baseline justify-between gap-3 py-3 border-b border-stone-100 last:border-b-0">
        <div className="flex items-baseline gap-2">
          <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
        </div>
        <span className="text-xs text-stone-400 italic" data-testid={`${testidPrefix}-empty`}>
          Not sent yet
        </span>
      </div>
    );
  }
  const opened = (latest.open_count || 0) > 0;
  return (
    <div className="py-3 border-b border-stone-100 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
        <span className="text-[11px] text-stone-500" title={absShort(latest.sent_at)}>
          Sent {relTime(latest.sent_at)}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span
          className={`inline-flex items-center gap-1 text-sm font-semibold ${opened ? "text-stone-900" : "text-stone-400"}`}
          data-testid={`${testidPrefix}-opens`}
        >
          <Eye className="h-3.5 w-3.5" />
          {latest.open_count || 0} open{(latest.open_count || 0) === 1 ? "" : "s"}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-sm font-semibold ${(latest.click_count || 0) > 0 ? "text-stone-900" : "text-stone-400"}`}
          data-testid={`${testidPrefix}-clicks`}
        >
          <MousePointerClick className="h-3.5 w-3.5" />
          {latest.click_count || 0} click{(latest.click_count || 0) === 1 ? "" : "s"}
        </span>
        {opened && latest.last_opened_at && (
          <span className="inline-flex items-center gap-1 text-[11px] text-stone-500" title={absShort(latest.last_opened_at)}>
            <Clock className="h-3 w-3" />
            Last opened {relTime(latest.last_opened_at)}
          </span>
        )}
      </div>
    </div>
  );
}

export default function EmailEngagementCard({ listingId, refreshKey = 0 }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${listingId}/email-stats`);
        if (alive) setStats(data);
      } catch (e) {
        if (alive) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load stats");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [listingId, refreshKey]);

  if (loading) {
    return (
      <div className="rounded-md border border-stone-200 bg-white p-5 animate-pulse">
        <div className="h-4 w-32 bg-stone-100 rounded mb-4" />
        <div className="h-16 bg-stone-50 rounded" />
      </div>
    );
  }
  if (!stats) return null;
  const hasAny = (stats.daily.sends || 0) + (stats.weekly.sends || 0) > 0;

  return (
    <section
      data-testid="email-engagement-card"
      className="rounded-md border border-stone-200 bg-white p-5"
    >
      <div className="flex items-baseline gap-2 mb-1">
        <Mail className="h-4 w-4 text-stone-500" />
        <h3 className="text-sm font-semibold text-stone-900 tracking-tight">Email engagement</h3>
      </div>
      <p className="text-[11px] text-stone-500 mb-3">
        Latest digest performance — tracking starts on the next send.
      </p>
      {hasAny ? (
        <div>
          <Row label="Daily digest" latest={stats.daily.latest} testidPrefix="engagement-daily" />
          <Row label="Weekly recap" latest={stats.weekly.latest} testidPrefix="engagement-weekly" />
          <p className="text-[10px] text-stone-400 mt-3 italic">
            Opens are approximate — some email clients preload images automatically. Clicks are exact.
          </p>
        </div>
      ) : (
        <p className="text-xs text-stone-500 italic" data-testid="engagement-empty">
          No emails sent to this seller yet.
        </p>
      )}
    </section>
  );
}
